/**
 * Rutas de la Tienda del admin. La cabecera de cada pantalla usa el título
 * y la descripción que antes vivían en las pestañas de /store.
 * Las marcas de club viven en /brands, dentro del hub de Clubes.
 */

export interface StoreSection {
    /** Valor antiguo de ?tab=, para las redirecciones. */
    tab: string;
    path: string;
    title: string;
    description: string;
}

export const STORE_SECTIONS: readonly StoreSection[] = [
    {
        tab: 'orders',
        path: '/store/orders',
        title: 'Pedidos',
        description: 'Gestión de pedidos físicos pagados con Stripe',
    },
    {
        tab: 'products',
        path: '/store/products',
        title: 'Productos',
        description: 'Equipamiento de proveedores y merch propio',
    },
    {
        tab: 'suppliers',
        path: '/store/suppliers',
        title: 'Proveedores',
        description: 'Estado de la sync, selección de productos y plazos',
    },
    {
        tab: 'materials',
        path: '/store/affiliates',
        title: 'Afiliados',
        description: 'Catálogo de productos recomendados (afiliados)',
    },
    {
        tab: 'shipping',
        path: '/store/settings/shipping',
        title: 'Envíos',
        description: 'Tarifas por zona, paquetes y preparación del merch',
    },
    {
        tab: 'segunda-vida',
        path: '/store/second-life',
        title: 'Segunda Vida',
        description: 'Mercadillo: pedidos, tracking, cobro Stripe, denuncias y flags',
    },
    {
        tab: 'store-brands',
        path: '/store/settings/brands',
        title: 'Marcas de tienda',
        description: 'Datos del fabricante para los productos publicados',
    },
    {
        tab: 'partners',
        path: '/store/partners',
        title: 'Colaboradores',
        description: 'Fichas de la landing y de la tienda de la app (Marnific, Turismo Marinero…)',
    },
    {
        tab: 'payments',
        path: '/store/settings/payments',
        title: 'Pagos y facturación',
        description: 'Modo de Stripe, sincronización de precios y facturas emitidas',
    },
];

/** Pestañas que ya no tienen pantalla. La ruta antigua redirige. */
const LEGACY_TAB_REDIRECTS: Readonly<Record<string, string>> = {
    orders: '/store/orders',
    products: '/store/products',
    suppliers: '/store/suppliers',
    fichas: '/store/products',
    catalog: '/store/catalog',
    propios: '/store/products?origin=merch',
    materials: '/store/affiliates',
    shipping: '/store/settings/shipping',
    'segunda-vida': '/store/second-life',
    'store-brands': '/store/settings/brands',
    partners: '/store/partners',
    payments: '/store/settings/payments',
};

export interface StoreNavChild {
    label: string;
    path: string;
    end?: boolean;
}

export interface StoreNavGroup {
    label: string;
    items: readonly StoreNavChild[];
}

export const STORE_NAV_SECTIONS: readonly StoreNavGroup[] = [
    {
        label: 'Operar',
        items: [
            { label: 'Inicio', path: '/store', end: true },
            { label: 'Pedidos', path: '/store/orders' },
        ],
    },
    {
        label: 'Catálogo',
        items: [
            { label: 'Productos', path: '/store/products' },
            { label: 'Proveedores', path: '/store/suppliers' },
        ],
    },
    {
        label: 'Canales',
        items: [
            { label: 'Segunda Vida', path: '/store/second-life' },
            { label: 'Afiliados', path: '/store/affiliates' },
            { label: 'Colaboradores', path: '/store/partners' },
        ],
    },
    {
        label: 'Ajustes',
        items: [
            { label: 'Envíos', path: '/store/settings/shipping' },
            { label: 'Marcas de tienda', path: '/store/settings/brands' },
            { label: 'Pagos y facturación', path: '/store/settings/payments' },
        ],
    },
];

export function storeTabRedirectTarget(tab: string | null): string | null {
    if (tab && LEGACY_TAB_REDIRECTS[tab]) return LEGACY_TAB_REDIRECTS[tab];
    if (tab) return '/store/orders';
    return null;
}

/** Las fichas importadas quedan en revisión: la cola es esa vista, no «Todos». */
export function fichasImportQueuePath(): string {
    return '/store/products?view=review';
}

/** La cola de fichas abre la vista de Productos que cubre el mismo trabajo. */
export function fichasQueueRedirect(params: URLSearchParams): string {
    const quality = params.get('quality');
    const incomplete = params.get('incomplete') === '1';
    const view = incomplete || quality === 'hero'
        ? 'incomplete'
        : quality === 'draft'
            ? 'draft'
            : quality === 'review'
                ? 'review'
                : null;
    const next = new URLSearchParams();
    if (view) next.set('view', view);
    const q = params.get('q')?.trim();
    if (q) next.set('q', q);
    const supplier = params.get('supplier')?.trim();
    if (supplier) next.set('origin', supplier);
    const search = next.toString();
    return search ? `/store/products?${search}` : '/store/products';
}

/** Propios: el merch está en Productos; los colaboradores tienen su canal. */
export function ownPageRedirect(params: URLSearchParams): string {
    if (params.get('sub') === 'partners') return '/store/partners';
    return '/store/products?origin=merch';
}

export function storeSectionForPath(pathname: string): StoreSection | null {
    return STORE_SECTIONS.find((section) => section.path === pathname) ?? null;
}
